import ai from './ai'
import expandableList from './expandableList'
import rich_text_editor from './richTextEditor'
import zendesk from './zendesk'

// One string, three roles in grant_reward_modal below: the fallback heading, that
// heading's dialog name, and the reward radio's label. The keys stay separate
// (each is addressed on its own, and any one of them could be reworded without
// the others) but they read the same today, so they share a source rather than
// three literals that can silently drift. Same pattern as locales/recoveryAi.ts'
// module-level label maps.
const GRANT_A_REWARD = 'Grant a reward'

export default {
  ai,
  expandableList,
  rich_text_editor,
  zendesk,
  segment_state_badge: {
    label: 'In Review',
    tooltip_content:
      'This segment is currently under review. Campaigns using this segment cannot be activated until the segment is approved.',
  },
  confirm_modal: {
    cancel: 'Cancel',
  },
  cropper_modal: {
    upload_image: 'Upload image',
    image_requirement: 'Image requirement %{width} x %{height} px',
    change_image: 'Change image',
    cancel: 'Cancel',
    crop_and_upload: 'Crop and upload',
    preview: 'Preview',
  },
  no_search_results_found: {
    title: 'No results found',
    body: 'Try searching for something else',
  },
  search_input: {
    label: 'Search',
    clear: 'Clear',
  },
  side_nav: {
    other: 'Other',
  },
  text_expander: {
    show_more: 'Show %{count} more %{noun}',
  },
  update_banners: {
    replace: {
      title: 'This page is getting a new look and feel!',
      subtitle: 'Upcoming updates to Thanx include improvements to this page.',
    },
    beta: {
      title: 'This page is in beta',
      subtitle: 'You’ve got early access to an upcoming release.',
    },
    update: {
      title: 'Updates ahead',
      subtitle:
        'This page will be replaced by a different feature as part of upcoming improvements.',
    },
    delete: {
      title: 'Updates ahead',
      subtitle: 'This page will be removed as part of upcoming improvements.',
    },
    button: 'Learn about these updates',
    extra_subtitles: {
      july_23:
        'This report will be removed as part of upcoming improvements in July 2023.',
    },
  },
  help_center_link: {
    help: 'Help',
  },
  legal_docs_nag_modal: {
    title: 'Action required',
    description:
      'Your brand is missing required legal documentation. Under our updated service provider agreement, all brands must configure their own <strong>Privacy Policy</strong>, <strong>Terms & Conditions</strong>, <strong>Tracking Opt-Out Guide</strong>, and <strong>Suppression Notification Email</strong> to continue operating on the Thanx platform.<br/><br/>The deadline to provide this has <em>expired</em>. Our team has temporarily re-enabled your dashboard access. Continuing without these documents in place creates considerable legal risk for your organization and your access may be revoked soon if you do not complete this step.<br/><br/>This takes just a few minutes. You can use our provided templates or link to documents you host yourself.',
    setup_legal_docs: 'Set up legal docs',
    dismiss: 'Dismiss',
  },
  // NOTE: placeholder copy — OPE-908 rewrites it. Must stay accurate: this is
  // about authorizing Thanx to act on the merchant's Apple Developer ACCOUNT
  // (App Store Connect / license agreement), NOT customer data.
  credential_sharing_nag_modal: {
    // Case 1 — nag the merchant to let Thanx handle Apple on their behalf.
    grant: {
      title: 'Action required: Apple Developer account',
      description:
        'To keep your app live in the App Store, the items below must be handled in your <strong>Apple Developer account</strong>. This is about Apple’s program requirements — it does not grant access to your customer data.<br/><br/>The fastest option is to let Thanx handle it: review and sign the authorization and we’ll take care of everything below for you.',
      primary: 'Review & sign agreement',
      secondary: 'I’ll manage Apple myself',
      remind_later: 'Remind me later',
    },
    // Case 2 — the merchant has opted to manage Apple themselves.
    self_serve: {
      title: 'Complete these items in App Store Connect',
      description:
        'You’ve chosen to manage your <strong>Apple Developer account</strong> yourself. Complete the outstanding items below in App Store Connect to keep your app live. These relate to Apple’s program requirements — not your customer data.',
      secondary: 'Actually, let Thanx handle it',
      remind_later: 'Remind me later',
    },
    // One entry rendered per outstanding obligation.
    obligation: {
      membership: {
        title: 'Apple Developer Program membership',
        description:
          'Your Apple Developer Program membership ($99/year, billed by Apple) needs to be renewed. It expires on %{expiry}.',
        description_no_date:
          'Your Apple Developer Program membership ($99/year, billed by Apple) needs to be renewed.',
        cta: 'Renew in App Store Connect',
      },
      license_agreement: {
        title: 'Apple Developer Program License Agreement',
        description:
          'Apple has published an updated Developer Program License Agreement (free) that must be accepted before new versions of your app can be released.',
        cta: 'Accept in App Store Connect',
      },
    },
  },
  nps_transition_modal: {
    badge: 'Product update',
    active: {
      title: 'RecoveryAI is here!',
      body_1:
        '<strong>RecoveryAI</strong> is Thanx’s new <em>guest recovery</em> product - it automatically identifies issues after customers’ digital orders, resolves them automatically within settings you control, and drives measurable future visits.',
      primary_cta: 'Explore RecoveryAI',
    },
    pre_launch: {
      title: 'Orders 👍/👎 is here',
      body_1:
        '<strong>Launching soon: RecoveryAI</strong> is Thanx’s new <em>guest recovery</em> product - it automatically identifies issues after customers’ digital orders, resolves them automatically within settings you control, and drives measurable future visits.',
      primary_cta: 'Explore Orders 👍/👎',
    },
    body_2:
      'Your historical NPS reporting is still available for review and export for a limited time.',
    secondary_cta: 'Access NPS data',
  },
  credential_sharing_nag_banner: {
    banner_aria_label: 'Apple Developer account action required',
    message: 'Action needed to keep your app live in the App Store.',
    cta: 'Review',
  },
  legal_docs_block_modal: {
    title: 'Action required',
    description:
      'Your brand has not completed its required legal documentation. As of May 1, 2026, all brands must have a configured <strong>Privacy Policy</strong>, <strong>Terms &amp; Conditions</strong>, <strong>Tracking Opt-Out Guide</strong>, and <strong>Suppression Notification Email</strong> to access the Thanx dashboard.<br/><br/>Please complete the setup below to restore access. You can use our provided templates or link to documents you host yourself.<br/><br/>If you need assistance, contact your Customer Success Manager or email <a href="mailto:success@thanx.com">success@thanx.com</a>.',
    continue: 'Continue',
  },
  granted_reward_expiration_banner: {
    banner_aria_label: 'Granted reward expiration notice',
    message_prefix: 'Some of your granted rewards are set to never expire.',
    message_link: 'Set a global expiration date',
    message_suffix: 'to cap your outstanding liability.',
    dismiss_aria_label: 'Dismiss banner',
  },
  table_pagination: {
    // Native key / key_plural pair — react-redux-i18n picks the form from `count`
    // (it does NOT auto-pluralize _one/_other; see context/knowledge/i18n-pluralization.md).
    showing_results: 'Showing %{start}-%{end} of %{count} result',
    showing_results_plural: 'Showing %{start}-%{end} of %{count} results',
    // Stacked layout only (300px feedback inbox pane): bolds the numbers per the
    // prototype's `.fd-count b` treatment. Rendered via dangerouslySetInnerHTML,
    // same idiom as RecoveryAI/Activation/ActivationAgreement.tsx.
    showing_results_stacked:
      'Showing <b>%{start}-%{end}</b> of <b>%{count}</b> result',
    showing_results_stacked_plural:
      'Showing <b>%{start}-%{end}</b> of <b>%{count}</b> results',
    prev: 'Prev',
    next: 'Next',
    page: 'Page',
    of: 'of %{count}',
    page_input_aria: 'Page number',
    rows_per_page_label: 'Rows per page',
    rows_per_page_aria: 'Rows per page',
  },
  table_toolbar: {
    // searchAriaLabel and hideResolvedLabel arrive pre-translated from the scene
    // (worded per surface); these are generic across both surfaces and live here.
    all_locations: 'All locations',
    // baseui derives the Select's accessible name from its value ("Selected …");
    // without this label a screen reader never hears that it filters by location.
    filter_by_location: 'Filter by location',
  },
  // components/FeedbackRatingIcon — the thumb glyph in the Response column,
  // shared by FeedbackTable and RecentActivitiesTable (which each used to carry
  // their own identical copy). The labels name the RATING, worded to match the
  // "Response" column header they sit under.
  feedback_rating: {
    positive: 'Positive response',
    negative: 'Negative response',
  },
  feedback_table: {
    // The six always-on columns are identical on both consuming surfaces (RAI
    // Escalations and the TUTD Details tab), so the shared strings live here; only
    // genuinely per-surface copy (empty/error messages, search/hide-resolved
    // labels) stays a prop. `comment` is the exception: its column is opt-in
    // (showCommentColumn), because only the Details tab's wire carries a comment.
    headers: {
      response: 'Response',
      date: 'Date',
      customer: 'Customer',
      comment: 'Comment',
      location: 'Location',
      resolved_by: 'Resolved by',
      view: 'View',
    },
    // Value is load-bearing — FeedbackTable's Date SortableColumnHeader uses it
    // as the sort aria-label, and RAI Activity/index.test.tsx asserts on it.
    sort_by_date: 'Sort by date',
    // The quotes around a customer's free-text comment are user-visible
    // punctuation, so they live here rather than being hardcoded in JSX — a
    // locale that quotes differently (« », „ ") overrides the whole wrapper.
    comment_quoted: '“%{comment}”',
    view_aria: 'View %{customer}',
    // sr-only note in the visually-empty Resolved-by cell (an addition the design
    // cannot show).
    not_resolved: 'Not resolved',
    retry: 'Retry',
  },
  // Status pill shared by the RecoveryAI case detail and the Reports
  // order-rating detail page (moved out of `recovery_ai.activity.status` when
  // the badge was promoted to components/FeedbackStatusBadge).
  feedback_status_badge: {
    resolved: 'Resolved',
    manually_resolved: 'Manually Resolved',
    unresolved: 'Unresolved',
    escalated: 'Escalated',
    pending: 'Pending',
    // Order-rating-only, neutral copy — see ActivityStatus's
    // 'closed_by_operator' member for why.
    closed_by_operator: 'Closed by operator',
  },
  // components/OrderSummaryStrip + its components/ReceiptModal (moved out of
  // `recovery_ai.case_detail` when both were promoted for the Reports
  // order-rating detail page).
  order_summary: {
    order_heading: 'Order #%{number}',
    empty: '—',
    view_full_receipt: 'View full receipt',
    items_count_one: '%{count} item',
    items_count_other: '%{count} items',
    delivery_methods: {
      pickup: 'Pickup',
      delivery: 'Delivery',
      dine_in: 'Dine-in',
      curbside: 'Curbside',
      tableside: 'Tableside',
      drivethru: 'Drive-thru',
    },
    receipt: {
      heading: 'Order #%{orderNumber}',
      your_items: 'Your items',
    },
  },
  // components/OrderSnapshotReceipt — the item list + totals body shared by
  // components/ReceiptModal and FeedbackInbox/RightRail/OrderInfoPanel
  // (moved out of `order_summary.receipt` above when the rendering was
  // extracted into its own component).
  order_snapshot_receipt: {
    subtotal: 'Sub-Total:',
    tax: 'Tax:',
    promo: 'Promo',
    total: 'Order Total:',
    value_unavailable: '—',
  },
  // components/CustomerInfoCard (moved out of `recovery_ai.case_detail.customer`
  // when the panel was promoted for the Reports order-rating detail page).
  customer_info: {
    heading: 'Customer information',
    empty: '—',
    first_name: 'First name',
    last_name: 'Last name',
    email: 'Email',
    phone: 'Phone number',
    loyalty_tier: 'Loyalty tier',
    total_visits: 'Total visits',
    lifetime_spend: 'Lifetime spend',
    prior_resolutions: 'Prior customer resolution history',
    view_profile: 'View customer profile',
    // Read by AT when there's no profile to link to — deliberately not
    // "coming soon": that implied the gap was always temporary, but it also
    // covers a permanently unlinkable customer (no account, no view access).
    view_profile_disabled_hint: 'View customer profile (unavailable)',
    // Appended to the accessible name (not the visible label) when the link
    // opens in a new tab, so AT users get the context-change warning WCAG
    // 3.2.5 requires without changing the on-screen copy.
    view_profile_new_tab_hint: 'View customer profile (opens in new tab)',
  },
  // components/RecentActivitiesTable (moved out of
  // `recovery_ai.case_detail.recent_activities` when the panel was promoted for
  // the Reports order-rating detail page). The per-row View aria-label is NOT
  // here — it names the linked record, which is surface-specific, so each
  // caller passes its own.
  recent_activities: {
    heading: 'Recent activities',
    empty: '—',
    // Response (thumb) column (redesign phase 4) — the row's own rating. The
    // glyph and its aria-labels live in components/FeedbackRatingIcon
    // (`components.feedback_rating`), shared with FeedbackTable.
    response: 'Response',
    date: 'Date',
    sort_by_date: 'Sort by date',
    resolved_by: 'Resolved by',
    resolution: 'Resolution',
    sort_by_resolution: 'Sort by resolution',
    // Return-in-30-days column (redesign phase 4) — DATA-GATED on the CALLER,
    // not the backend: the wire field ships in #3981, but the whole column is
    // omitted until a caller forwards it (the Reports scene does in #3984; RAI
    // never has it), i.e. until a row's `returnedWithin30Days` is anything other
    // than `undefined`. See RecentActivitiesTable's `showReturnColumn`.
    return_30_days: {
      heading: 'Return in 30 days',
      // yes/no/pending double as the status-glyph aria-labels (green check /
      // orange ✕ / grey hollow circle) — the column renders icons, not text.
      yes: 'Yes',
      no: 'No',
      pending: 'Pending',
    },
    view: 'View',
    // The "View N more" expander (redesign phase 4) replacing the old hard
    // 5-row cap. `view_less` collapses it back — the toggle keeps aria-expanded
    // meaningful.
    view_more: 'View %{count} more',
    view_less: 'View less',
    // Frontend enum (backend maps nucleus values → these via
    // RESOLUTION_TYPE_TO_FRONTEND). Keep in sync with the wire enum, NOT the
    // raw nucleus values (reward_granted/points_granted/refunded).
    resolution_types: {
      resolved_with_reward: 'Reward granted',
      resolved_without_reward: 'Resolved without reward',
      no_action: 'No action',
      // Generic outcome for a surface whose rows carry only WHO resolved the
      // record, not how (the order-ratings index): a resolved row reads
      // "Resolved", an unresolved one falls to the em-dash.
      resolved: 'Resolved',
      // An operator's close on that same surface. Neutral by design — mirrors
      // the header's closed_by_operator badge and the timeline's "Closed" step,
      // since the wire can't tell a dismissal from a manual resolution.
      closed_by_operator: 'Closed',
    },
  },
  // Role labels for the shared message list (components/Transcript), used by
  // the RAI conversation transcript. The Reports order-rating detail page
  // used this component pre-redesign; it now renders the comment as its own
  // blockquote card instead (order_ratings.rating_detail.customer_response).
  transcript: {
    roles: {
      user: 'Customer',
      ai: 'RecoveryAI',
      system: 'System',
      tool_call: 'System',
    },
  },
  // components/GrantRewardModal (moved out of
  // `recovery_ai.case_detail.grant_reward` when the modal was promoted for the
  // Reports order-rating detail page). The inline failure copy is NOT here — it
  // names the record being resolved ("this case" / "this feedback"), so each
  // caller passes its own `errorMessage`.
  grant_reward_modal: {
    // Named heading, identical to the customer-profile grant modal's
    // (users.view.grant_modal.title) — the two surfaces grant the same way and
    // now read the same. %{name} = the customer's display name.
    title: 'Grant reward to %{name}',
    // Heading / dialog name when the surface has no customer name to put in the
    // title (a rating whose customer record is gone, or a conversation with
    // neither first nor last name).
    heading: GRANT_A_REWARD,
    aria_label: GRANT_A_REWARD,
    // Accessible name for the reward/points radio group (it has no visible
    // label of its own).
    grant_type_aria: 'What to grant',
    // The two mutually exclusive grant paths — radio labels.
    grant_a_reward: GRANT_A_REWARD,
    grant_points: 'Grant points',
    // Proceed button on the reward path; the points path reuses grant_points
    // above, mirroring users.view.grant_modal.
    grant_reward: 'Grant reward',
    select_reward: 'Select reward',
    reward_placeholder: 'Select a reward',
    add_message: 'Add a message',
    // Confirm's disabled-button tooltip when the required message is blank —
    // TooltipWhenDisabled via ModalContainer's proceedTooltip. Not shown for
    // the other disabled-Confirm reasons (no reward/points chosen), which are
    // already visually self-evident.
    message_required_tooltip: 'Add a message before granting',
    // %{email} = the LOGGED-IN OPERATOR's email (not the customer's): the
    // Reply-To on the email the backend sends. Wording is deliberately verbatim
    // from resolve_with_reply_modal.reply_to_footer — the grant message now
    // travels the same reply path (reply_message -> Email::Template::
    // FeedbackResponse), so the two dialogs must not describe it differently.
    reply_to_footer: 'Replies to your message will go directly to %{email}',
    points_label: 'Points',
    points_placeholder: 'Enter a points amount',
    points_helper: 'Grant bonus points to the customer instead of a reward.',
    no_rewards: 'No rewards are available to grant.',
    loading: 'Loading rewards…',
    // The reward field's own load failure — deliberately distinct from
    // no_rewards, which a broken catalog used to be indistinguishable from
    // (AI-1017). Reopening the modal DOES retry: the query is skipped while the
    // modal is closed, and RTK Query re-runs the thunk on the next subscribe
    // because a rejected cache entry carries no fulfilledTimeStamp.
    rewards_error:
      'We couldn’t load the rewards list. Close and reopen this window to try again.',
  },
  // components/ResolveWithReplyModal — the confirmation the "Mark as resolved"
  // action opens on both surfaces (RecoveryAI case detail, Reports order-rating
  // detail). The inline failure copy is NOT here — it names the record being
  // resolved ("this case" / "this feedback"), so each caller passes its own
  // `errorMessage`.
  resolve_with_reply_modal: {
    // Heading and, through ModalContainer, the dialog's accessible name.
    title: 'Would you like to also send a message to this customer?',
    // The reply is optional — resolving happens either way — so the label says
    // so rather than relying on the operator inferring it.
    reply_label: 'Your reply (optional)',
    // %{email} = the LOGGED-IN OPERATOR's email (not the customer's): the
    // Reply-To on the message the backend sends.
    reply_to_footer: 'Replies to your message will go directly to %{email}',
    // Proceed button. Same copy as the trigger that opened the modal, so the
    // operator confirms the action they asked for.
    proceed: 'Mark as resolved',
  },
  // Shared by RecoveryAI's CaseDetail and the Order Feedback rating detail. The
  // email is the same on both; the side effect is caller-defined via
  // `resolvesRecord` — send-only for Order Feedback, send-and-resolve for the
  // RAI case (see send_and_resolve / resolve_note below).
  send_message_modal: {
    // %{name} = the customer's display name (RAI's ConversationCustomer.name /
    // the order-feedback user's name). `heading` is the fallback when it's null.
    title: 'Send message to %{name}',
    heading: 'Send message',
    customer_email_label: 'Customer email',
    // `absent`: genuinely no linked recipient — the send is impossible.
    no_email: 'No email on file',
    no_email_reason:
      'This customer has no email on file, so a message can’t be sent.',
    // `masked`: the record has a recipient the backend resolves server-side,
    // but this operator can’t see the address (PII consent / unfetchable
    // profile). The message still delivers, so Send stays enabled.
    masked_email: 'Email hidden',
    masked_reason:
      'This customer’s email is hidden, but your message will still be delivered to them.',
    // `loading`: the address fetch is still in flight (rating profile).
    loading_email: 'Loading customer email…',
    copy: 'Copy',
    email_copied: 'Email copied to clipboard',
    email_copy_failed: 'We couldn’t copy the email to your clipboard.',
    subject_label: 'Subject',
    subject_placeholder: "What's this message about?",
    message_label: 'Message',
    message_placeholder: 'Write your message to the customer',
    // %{email} = the LOGGED-IN OPERATOR's email (not the customer's): the
    // Reply-To the backend sets on the send-only email (Email::Template::
    // FeedbackOperatorMessage / RecoveryOperatorMessage #reply_to_address).
    // Verbatim from resolve_with_reply_modal.reply_to_footer — all three
    // message-sending dialogs on these surfaces share the same reply path, so
    // they must not describe it differently.
    reply_to_footer: 'Replies to your message will go directly to %{email}',
    send: 'Send',
    // Confirm button + body note for the send-and-resolve caller (RAI case
    // detail): the send closes the case too, so the label and note say so.
    send_and_resolve: 'Send and resolve',
    resolve_note: 'Sending this message will also mark the case as resolved.',
  },
}
